import React, { useEffect, useState } from 'react';
import { semanticBrandingService } from '../../services/semanticBranding';
interface AnalysisProgressBarProps {
  isAnalyzing: boolean;
  onComplete: () => void;
  duration?: number; // in seconds
  documentId: string; // Added document ID to check status
}
const AnalysisProgressBar: React.FC<AnalysisProgressBarProps> = ({
  isAnalyzing,
  onComplete,
  duration = 180,
  // default 3 minutes (180 seconds)
  documentId
}) => {
  const [progress, setProgress] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(duration);
  const [elapsedTime, setElapsedTime] = useState(0);
  useEffect(() => {
    if (!isAnalyzing) {
      setProgress(0);
      setSecondsLeft(duration);
      setElapsedTime(0);
      return;
    }
    console.log(`[AnalysisProgressBar] Monitoring analysis progress for document ID: ${documentId}`);
    const interval = 100; // Update every 100ms for smoother animation
    const steps = duration * 1000 / interval;
    const increment = 100 / steps;
    let currentProgress = 0;
    let timeLeft = duration;
    let elapsed = 0;
    // Timer for visual progress
    const progressTimer = setInterval(() => {
      currentProgress += increment;
      timeLeft -= interval / 1000;
      elapsed += interval / 1000;
      setElapsedTime(elapsed);
      if (currentProgress >= 100) {
        clearInterval(progressTimer);
        setProgress(100);
        setSecondsLeft(0);
        // Força a conclusão da análise após o tempo máximo
        console.log('[AnalysisProgressBar] Maximum time reached, forcing completion');
        onComplete();
      } else {
        setProgress(currentProgress);
        setSecondsLeft(Math.ceil(timeLeft));
      }
    }, interval);
    // Start checking status immediately, then increase frequency over time
    let statusCheckTimer: NodeJS.Timeout | null = null;
    // Check immediately after 2 seconds (reduzido de 5 para verificar mais cedo)
    const initialCheck = setTimeout(() => {
      console.log(`[AnalysisProgressBar] Initial status check at 2 seconds`);
      checkAnalysisStatus();
      // Then check every 5 seconds until 30 seconds (reduzido de 10s para 5s)
      statusCheckTimer = setInterval(() => {
        console.log(`[AnalysisProgressBar] Status check at ${elapsed.toFixed(0)} seconds (5s interval)`);
        checkAnalysisStatus();
        // After 30 seconds, check every 3 seconds (reduzido de 5s para 3s)
        if (elapsed >= 30 && statusCheckTimer) {
          console.log(`[AnalysisProgressBar] Increasing check frequency to 3s interval`);
          clearInterval(statusCheckTimer);
          statusCheckTimer = setInterval(() => {
            console.log(`[AnalysisProgressBar] Status check at ${elapsed.toFixed(0)} seconds (3s interval)`);
            checkAnalysisStatus();
            // After 60 seconds, check every 1 second
            if (elapsed >= 60 && statusCheckTimer) {
              console.log(`[AnalysisProgressBar] Increasing check frequency to 1s interval`);
              clearInterval(statusCheckTimer);
              statusCheckTimer = setInterval(() => {
                console.log(`[AnalysisProgressBar] Status check at ${elapsed.toFixed(0)} seconds (1s interval)`);
                checkAnalysisStatus();
              }, 1000);
            }
          }, 3000);
        }
      }, 5000);
    }, 2000);
    // Function to check if analysis is complete
    const checkAnalysisStatus = async () => {
      try {
        console.log(`[AnalysisProgressBar] Checking analysis status for document: ${documentId}`);
        const response = await semanticBrandingService.getBrandDocument(documentId);
        // Log completo do documento para depuração
        console.log(`[AnalysisProgressBar] Full document:`, response.brandDocument);
        console.log(`[AnalysisProgressBar] Document status:`, {
          step: response.brandDocument.step,
          hasAnaliseComparativa: !!response.brandDocument.metadata?.analiseComparativa,
          ultimaAnalise: response.brandDocument.ultimaAnalise
        });
        // Condição ampliada para verificar se a análise está completa
        // Verificação principal: step === 'concluido'
        if (response.brandDocument.step === 'concluido' || response.brandDocument.step === 'analise' || response.brandDocument.metadata && response.brandDocument.metadata.analiseComparativa && Object.keys(response.brandDocument.metadata.analiseComparativa).length > 0 || response.brandDocument.ultimaAnalise || elapsed > 120 // Força conclusão após 2 minutos se ainda estiver verificando
        ) {
          console.log(`[AnalysisProgressBar] Analysis complete! Calling onComplete()`);
          // Analysis is complete, clean up and notify
          if (progressTimer) clearInterval(progressTimer);
          if (statusCheckTimer) clearInterval(statusCheckTimer);
          if (initialCheck) clearTimeout(initialCheck);
          setProgress(100);
          setSecondsLeft(0);
          onComplete();
        }
      } catch (error) {
        console.error('[AnalysisProgressBar] Error checking analysis status:', error);
        // Se ocorrer erro após muito tempo tentando, considerar como concluído
        if (elapsed > 150) {
          console.log('[AnalysisProgressBar] Errors occurring for too long, forcing completion');
          if (progressTimer) clearInterval(progressTimer);
          if (statusCheckTimer) clearInterval(statusCheckTimer);
          if (initialCheck) clearTimeout(initialCheck);
          setProgress(100);
          setSecondsLeft(0);
          onComplete();
        }
      }
    };
    return () => {
      console.log(`[AnalysisProgressBar] Cleaning up timers`);
      clearInterval(progressTimer);
      clearTimeout(initialCheck);
      if (statusCheckTimer) clearInterval(statusCheckTimer);
    };
  }, [isAnalyzing, duration, onComplete, documentId]);
  // Format seconds to MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  if (!isAnalyzing) return null;
  return <div className="w-full p-4 bg-white rounded-lg shadow-sm border border-gray-100 mb-6" data-id="element-881" data-mp="3j3p7oadvcptg">
      <div className="flex justify-between items-center mb-2" data-id="element-882" data-mp="3nunsnn9syc31">
        <h3 className="font-medium text-gray-900" data-id="element-883" data-mp="3nvfkcn0a3po1">
          Analisando dados semânticos
        </h3>
        <span className="text-sm text-gray-600" data-id="element-884" data-mp="3ssyx1ihbvudv">
          Tempo restante: {formatTime(secondsLeft)}
        </span>
      </div>
      <div className="w-full bg-gray-100 rounded-full h-4 overflow-hidden" data-id="element-885" data-mp="6mjy1g1ggazg">
        <div className="bg-[#1649ff] h-4 rounded-full transition-all duration-100 ease-linear" style={{
        width: `${progress}%`
      }} data-id="element-886" data-mp="6kfvp2j7fk1a"></div>
      </div>
      <div className="mt-2 text-sm text-gray-600" data-id="element-887" data-mp="1m7ksxwbarvew">
        <p data-id="element-888" data-mp="1h5lqqpran4jj">
          Estamos processando os dados da sua marca e concorrentes. Isso pode
          levar até 3 minutos.
        </p>
      </div>
    </div>;
};
export default AnalysisProgressBar;